import { useState } from "react";
import {
    ShieldCheck,
    Fingerprint,
    KeyRound,
    Copy,
    Check,
    Plus,
    Download,
} from "lucide-react";
import { C, sBtn, sInp, sShell, sPanel, sGrid, sGlow } from "../styles";

/* Two paths:
   NEW:     choice → pin → bio? → recovery → done
   RESTORE: choice → phrase → pin (new on this device) → bio? → done
*/

export default function Setup({
    bioSupported,
    onCompleteNew,
    onCompleteRestore,
    generateRecoveryPhrase,
    registerBiometric,
    showToast,
}) {
    const [mode, setMode] = useState(null); // "new" | "restore"
    const [step, setStep] = useState("choice");

    // Shared state
    const [pin, setPin] = useState("");
    const [pinConfirm, setPinConfirm] = useState("");
    const [pinHint, setPinHint] = useState("");
    const [pinError, setPinError] = useState("");
    const [bioRawId, setBioRawId] = useState(null);
    const [registering, setRegistering] = useState(false);
    const [finishing, setFinishing] = useState(false);

    // NEW-specific
    const [phrase] = useState(() => generateRecoveryPhrase());
    const [phraseCopied, setPhraseCopied] = useState(false);
    const [phraseSaved, setPhraseSaved] = useState(false);

    // RESTORE-specific
    const [restorePhrase, setRestorePhrase] = useState("");
    const [restoreErr, setRestoreErr] = useState("");

    const gotoNew = () => {
        setMode("new");
        setStep("pin");
    };
    const gotoRestore = () => {
        setMode("restore");
        setStep("restore-phrase");
    };

    // ── New flow handlers ──
    const submitPin = () => {
        setPinError("");
        if (!/^\d{6,}$/.test(pin)) {
            setPinError("PIN must be at least 6 digits");
            return;
        }
        if (pin !== pinConfirm) {
            setPinError("PINs don't match");
            return;
        }
        setStep(bioSupported ? "bio" : mode === "new" ? "recovery" : "done");
    };

    const enrollBio = async () => {
        setRegistering(true);
        const raw = await registerBiometric();
        setRegistering(false);
        if (raw) {
            setBioRawId(raw);
            showToast("Biometric registered");
            setStep(mode === "new" ? "recovery" : "restore-finish");
        } else {
            showToast("Biometric setup cancelled or unavailable");
        }
    };

    const skipBio = () => {
        setStep(mode === "new" ? "recovery" : "restore-finish");
    };

    const finishNew = async () => {
        setFinishing(true);
        await onCompleteNew({
            pin,
            pinHint: pinHint.trim() || null,
            bioRawId,
            recoveryPhrase: phrase,
        });
        setFinishing(false);
    };

    const copyPhrase = async () => {
        try {
            await navigator.clipboard?.writeText(phrase);
            setPhraseCopied(true);
            setTimeout(() => setPhraseCopied(false), 1800);
        } catch {
            showToast("Copy not allowed — write the phrase manually");
        }
    };

    // ── Restore flow handlers ──
    const submitRestorePhrase = () => {
        const words = restorePhrase.trim().split(/\s+/).filter(Boolean);
        if (words.length < 12) {
            setRestoreErr("Enter all 12 words separated by spaces");
            return;
        }
        setRestoreErr("");
        setStep("restore-pin");
    };

    const finishRestore = async ({ withBio }) => {
        setFinishing(true);
        const ok = await onCompleteRestore({
            phrase: restorePhrase,
            newPin: pin,
            newPinHint: pinHint.trim() || null,
            enrollBio: !!withBio && !!bioRawId,
            bioRawId,
        });
        setFinishing(false);
        if (!ok) {
            // Restore failed — show error and go back to phrase step
            setRestoreErr("Restore failed — check your recovery phrase");
            setStep("restore-phrase");
        }
    };

    return (
        <div style={sShell}>
            <div style={sGrid} />
            <div style={sGlow} />
            <div style={sPanel} data-testid="setup-panel">
                {step === "choice" && (
                    <Choice onNew={gotoNew} onRestore={gotoRestore} />
                )}

                {/* NEW FLOW */}
                {mode === "new" && step === "pin" && (
                    <PinStep
                        title="Create PIN"
                        subtitle="6 digits. Used as a daily fallback when Face ID isn't available. Never stored — we only keep an encrypted verifier."
                        pin={pin}
                        setPin={setPin}
                        pinConfirm={pinConfirm}
                        setPinConfirm={setPinConfirm}
                        pinHint={pinHint}
                        setPinHint={setPinHint}
                        pinError={pinError}
                        onNext={submitPin}
                        stepLabel="Step 1 / 3"
                    />
                )}
                {mode === "new" && step === "bio" && (
                    <BioStep
                        stepLabel="Step 2 / 3"
                        title="Add Face ID?"
                        subtitle="Your device supports biometric unlock. This is the fastest and most secure way in. You can skip this if you prefer PIN-only."
                        registering={registering}
                        onEnroll={enrollBio}
                        onSkip={skipBio}
                    />
                )}
                {mode === "new" && step === "recovery" && (
                    <RecoveryStep
                        phrase={phrase}
                        copied={phraseCopied}
                        onCopy={copyPhrase}
                        saved={phraseSaved}
                        setSaved={setPhraseSaved}
                        onFinish={finishNew}
                        finishing={finishing}
                    />
                )}

                {/* RESTORE FLOW */}
                {mode === "restore" && step === "restore-phrase" && (
                    <RestorePhraseStep
                        phrase={restorePhrase}
                        setPhrase={setRestorePhrase}
                        error={restoreErr}
                        onNext={submitRestorePhrase}
                    />
                )}
                {mode === "restore" && step === "restore-pin" && (
                    <PinStep
                        title="Set a PIN for this device"
                        subtitle="Pick a PIN to use on this device. You can keep the same PIN you used on other devices (recommended), or choose a new one."
                        pin={pin}
                        setPin={setPin}
                        pinConfirm={pinConfirm}
                        setPinConfirm={setPinConfirm}
                        pinHint={pinHint}
                        setPinHint={setPinHint}
                        pinError={pinError}
                        onNext={() => {
                            setPinError("");
                            if (!/^\d{6,}$/.test(pin)) {
                                setPinError("PIN must be at least 6 digits");
                                return;
                            }
                            if (pin !== pinConfirm) {
                                setPinError("PINs don't match");
                                return;
                            }
                            setStep(bioSupported ? "bio" : "restore-finish");
                        }}
                        stepLabel="Step 2 / 3"
                    />
                )}
                {mode === "restore" && step === "bio" && (
                    <BioStep
                        stepLabel="Step 3 / 3"
                        title="Add Face ID on this device?"
                        subtitle="Each device enrolls its own biometric. Other devices stay independent — they keep their own Face ID, PIN or recovery access."
                        registering={registering}
                        onEnroll={enrollBio}
                        onSkip={skipBio}
                    />
                )}
                {mode === "restore" && step === "restore-finish" && (
                    <RestoreConfirmStep
                        finishing={finishing}
                        onFinish={() => finishRestore({ withBio: !!bioRawId })}
                    />
                )}
            </div>
        </div>
    );
}

// ───────── Sub-components ─────────
function Choice({ onNew, onRestore }) {
    return (
        <>
            <div style={labelStyle}>HIKARI LABS</div>
            <h1 style={h1Style}>Vault</h1>
            <p style={subStyle}>
                An encrypted, offline-first vault for your credentials,
                keys and private notes. Zero-knowledge — your data never
                leaves any device in plaintext.
            </p>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    margin: "18px 0 26px",
                }}
            >
                <Feature icon={<ShieldCheck size={14} />} text="AES-256-GCM encryption" />
                <Feature icon={<Fingerprint size={14} />} text="Face ID / Touch ID unlock" />
                <Feature icon={<KeyRound size={14} />} text="12-word recovery phrase" />
            </div>
            <button
                style={sBtn("primary")}
                onClick={onNew}
                data-testid="setup-create-new-btn"
            >
                <Plus size={14} style={{ verticalAlign: "-2px", marginRight: 8 }} />
                Create a new vault
            </button>
            <button
                style={sBtn("secondary")}
                onClick={onRestore}
                data-testid="setup-restore-btn"
            >
                <Download size={14} style={{ verticalAlign: "-2px", marginRight: 8 }} />
                Restore existing vault
            </button>
        </>
    );
}

function Feature({ icon, text }) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                color: C.muted,
                fontSize: 12,
            }}
        >
            <span style={{ color: C.accent }}>{icon}</span>
            {text}
        </div>
    );
}

function PinStep({
    title,
    subtitle,
    stepLabel,
    pin,
    setPin,
    pinConfirm,
    setPinConfirm,
    pinHint,
    setPinHint,
    pinError,
    onNext,
}) {
    return (
        <>
            <div style={labelStyle}>{stepLabel}</div>
            <h1 style={h1Style}>{title}</h1>
            <p style={subStyle}>{subtitle}</p>
            <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                maxLength={12}
                placeholder="••••••"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                style={sInp(true)}
                data-testid="setup-pin-input"
            />
            <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                maxLength={12}
                placeholder="confirm"
                value={pinConfirm}
                onChange={(e) =>
                    setPinConfirm(e.target.value.replace(/\D/g, ""))
                }
                onKeyDown={(e) => e.key === "Enter" && onNext()}
                style={sInp(true)}
                data-testid="setup-pin-confirm-input"
            />
            <input
                type="text"
                placeholder="Optional hint (shown on the lock screen)"
                value={pinHint}
                onChange={(e) => setPinHint(e.target.value)}
                maxLength={40}
                style={{ ...sInp(), marginTop: 6 }}
                data-testid="setup-pin-hint-input"
            />
            {pinError && (
                <div
                    style={{
                        color: C.danger,
                        fontSize: 12,
                        marginBottom: 10,
                    }}
                    data-testid="setup-pin-error"
                >
                    {pinError}
                </div>
            )}
            <button
                style={sBtn("primary")}
                onClick={onNext}
                data-testid="setup-pin-next-btn"
            >
                Continue
            </button>
        </>
    );
}

function BioStep({ stepLabel, title, subtitle, registering, onEnroll, onSkip }) {
    return (
        <>
            <div style={labelStyle}>{stepLabel}</div>
            <h1 style={h1Style}>{title}</h1>
            <p style={subStyle}>{subtitle}</p>
            <button
                style={sBtn("primary")}
                onClick={onEnroll}
                disabled={registering}
                data-testid="setup-bio-enroll-btn"
            >
                <Fingerprint
                    size={14}
                    style={{ verticalAlign: "-2px", marginRight: 8 }}
                />
                {registering ? "Registering…" : "Enable Face ID / Touch ID"}
            </button>
            <button
                style={sBtn("ghost")}
                onClick={onSkip}
                data-testid="setup-bio-skip-btn"
            >
                Skip for now
            </button>
        </>
    );
}

function RecoveryStep({
    phrase,
    copied,
    onCopy,
    saved,
    setSaved,
    onFinish,
    finishing,
}) {
    const words = phrase.split(" ");
    return (
        <>
            <div style={labelStyle}>Step 3 / 3</div>
            <h1 style={h1Style}>Your recovery phrase</h1>
            <p style={subStyle}>
                Write this down now. It's the{" "}
                <b style={{ color: C.text }}>only</b> way to recover or
                access the vault on another device. We cannot recover it
                for you.
            </p>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 8,
                    background: "rgba(11,126,163,0.06)",
                    border: `1px dashed ${C.accent}55`,
                    borderRadius: 12,
                    padding: 14,
                    margin: "6px 0 14px",
                }}
                data-testid="setup-recovery-grid"
            >
                {words.map((w, i) => (
                    <div
                        key={i}
                        style={{
                            fontSize: 12,
                            color: C.text,
                            letterSpacing: "0.04em",
                        }}
                    >
                        <span style={{ color: C.dim, marginRight: 6 }}>
                            {String(i + 1).padStart(2, "0")}
                        </span>
                        {w}
                    </div>
                ))}
            </div>

            <button
                style={{ ...sBtn("secondary"), marginBottom: 8 }}
                onClick={onCopy}
                data-testid="setup-recovery-copy-btn"
            >
                {copied ? (
                    <>
                        <Check size={13} style={{ verticalAlign: "-2px", marginRight: 8 }} />
                        Copied
                    </>
                ) : (
                    <>
                        <Copy size={13} style={{ verticalAlign: "-2px", marginRight: 8 }} />
                        Copy phrase
                    </>
                )}
            </button>

            <label
                style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    margin: "16px 0",
                    cursor: "pointer",
                    fontSize: 12,
                    color: C.muted,
                }}
            >
                <input
                    type="checkbox"
                    checked={saved}
                    onChange={(e) => setSaved(e.target.checked)}
                    style={{ marginTop: 2 }}
                    data-testid="setup-recovery-confirm-checkbox"
                />
                I have saved these 12 words somewhere safe and understand
                there is no way to recover the vault without them.
            </label>

            <button
                style={{
                    ...sBtn("primary"),
                    opacity: saved && !finishing ? 1 : 0.35,
                    pointerEvents: saved && !finishing ? "auto" : "none",
                }}
                onClick={onFinish}
                disabled={!saved || finishing}
                data-testid="setup-finish-btn"
            >
                {finishing ? "Creating vault…" : "Create Vault"}
            </button>
        </>
    );
}

function RestorePhraseStep({ phrase, setPhrase, error, onNext }) {
    const wordCount = phrase.trim().split(/\s+/).filter(Boolean).length;
    return (
        <>
            <div style={labelStyle}>Step 1 / 3</div>
            <h1 style={h1Style}>Enter recovery phrase</h1>
            <p style={subStyle}>
                Type the 12 words from your existing vault, separated by
                spaces. We'll pull your encrypted vault from the cloud and
                decrypt it here.
            </p>
            <textarea
                placeholder="word1 word2 word3 …"
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                rows={4}
                style={{
                    ...sInp(),
                    minHeight: 110,
                    resize: "vertical",
                    letterSpacing: "normal",
                    textAlign: "left",
                    fontSize: 14,
                }}
                autoFocus
                data-testid="setup-restore-phrase-input"
            />
            <div
                style={{
                    fontSize: 11,
                    color: wordCount === 12 ? C.ok : C.dim,
                    marginBottom: 14,
                    letterSpacing: "0.1em",
                }}
                data-testid="setup-restore-word-count"
            >
                {wordCount} / 12 WORDS
            </div>
            {error && (
                <div
                    style={{
                        color: C.danger,
                        fontSize: 12,
                        marginBottom: 10,
                    }}
                    data-testid="setup-restore-error"
                >
                    {error}
                </div>
            )}
            <button
                style={{
                    ...sBtn("primary"),
                    opacity: wordCount >= 12 ? 1 : 0.4,
                    pointerEvents: wordCount >= 12 ? "auto" : "none",
                }}
                onClick={onNext}
                data-testid="setup-restore-phrase-next-btn"
            >
                Continue
            </button>
        </>
    );
}

function RestoreConfirmStep({ onFinish, finishing }) {
    return (
        <>
            <div style={labelStyle}>Final step</div>
            <h1 style={h1Style}>Ready to restore</h1>
            <p style={subStyle}>
                We're about to decrypt your vault and sync it to this
                device. Your other devices are not affected.
            </p>
            <button
                style={sBtn("primary")}
                onClick={onFinish}
                disabled={finishing}
                data-testid="setup-restore-finish-btn"
            >
                {finishing ? "Restoring…" : "Restore my vault"}
            </button>
        </>
    );
}

const labelStyle = {
    fontSize: 10,
    letterSpacing: "0.3em",
    color: C.accent,
    textTransform: "uppercase",
    marginBottom: 10,
};
const h1Style = {
    fontSize: 28,
    fontWeight: 300,
    color: C.text,
    letterSpacing: "-0.02em",
    marginBottom: 8,
};
const subStyle = {
    fontSize: 13,
    color: C.muted,
    marginBottom: 24,
    lineHeight: 1.6,
};
